/**
 * File: ProtectedRoute.tsx
 * Path: apps/admin/src/modules/auth/components/ProtectedRoute.tsx
 * Description: Route wrapper enforcing authentication and RBAC. The decision
 * itself lives in routeAccess.ts so it can be tested without a router.
 */

import React from "react";
import { Navigate } from "react-router-dom";
import { useProtectedRouteController } from "@/modules/auth/hooks/useProtectedRouteController";
import type { Permission } from "@idk/types";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** The permission the route needs; any staff role passes without one. */
  requires?: Permission;
}

/**
 * Prevents access to nested routes if the user session is missing, the account
 * has been deactivated, or the user lacks the required role.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requires,
}) => {
  const { decision, location } = useProtectedRouteController(requires);

  if (decision.kind === "pending") return null;

  if (decision.kind === "redirect") {
    return (
      <Navigate
        to={decision.to}
        state={decision.to === "/login" ? { from: location } : undefined}
        replace
      />
    );
  }

  return <>{children}</>;
};
